import parse from 'html-react-parser';
import { useMemo } from 'react';
import TailwindTheme from 'tailwindcss/defaultTheme';

import { mapMaverickDemoStoreKey } from 'src/api/methods';
import type { MerchantFontSizeEnum, UseCase } from 'src/api/models';
export function sleep(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}

export function assertString(val: unknown): asserts val is string {
  if (!val || typeof val !== 'string') {
    // oxlint-disable-next-line typescript/restrict-template-expressions
    throw new Error(`unexpected non string value: ${val}`);
  }
}

export function assertDefined<T>(val: undefined | null | T): asserts val is T {
  if (val === undefined || val === null) {
    throw new Error('unexpected undefined or null');
  }
}

export function fileExtension(filename: string): string {
  return filename
    .slice(((filename.lastIndexOf('.') - 1) >>> 0) + 2)
    .toLowerCase()
    .trim();
}

export function parseHtmlStringToReact(str: string) {
  return parse(str);
}

export function useParsedHtmlString(str: string | undefined): React.ReactNode {
  // Parsing is pure, so derive it during render rather than round-tripping through state in an
  // effect. That also drops the first-render `null`, which used to flash empty content in.
  return useMemo(() => (str ? parse(str) : null), [str]);
}

export function isUseCaseAbandonedCart(useCase: UseCase): boolean {
  return useCase.name.includes('abandoned');
}

export function removeNulls<T>(obj: T | null | undefined): obj is T {
  return obj !== null || obj !== undefined;
}

export function mapFontSizes(merchangeFontSize: MerchantFontSizeEnum): `text-${keyof typeof TailwindTheme.fontSize}` {
  type FontSizeRawToTwnd = Record<MerchantFontSizeEnum, `text-${keyof typeof TailwindTheme.fontSize}`>;
  const fontSizeRawToTwnd: FontSizeRawToTwnd = {
    XS: 'text-xs',
    SM: 'text-sm',
    BASE: 'text-base',
    LG: 'text-lg',
    XL: 'text-xl',
    TWO_XL: 'text-2xl',
    THREE_XL: 'text-3xl',
    FOUR_XL: 'text-4xl',
  };
  return fontSizeRawToTwnd[merchangeFontSize];
}

export function replaceDemoStoreName(text: string, storeKeyName: string | undefined): string {
  const isMaverickDemo = storeKeyName && storeKeyName in mapMaverickDemoStoreKey;
  if (!isMaverickDemo) {
    return text;
  }
  return text
    .replaceAll('Sassy Saints', 'Maverick')
    .replaceAll('Sassy', 'Maverick')
    .replaceAll('SASSY', 'MAVERICK')
    .replaceAll('Cakesbody', 'Maverick')
    .replaceAll('Cakes Body', 'Maverick')
    .replaceAll('CAKESBODY', 'MAVERICK')
    .replaceAll('CAKES BODY', 'MAVERICK')
    .replaceAll('Magic Mind', 'Maverick')
    .replaceAll('MAGIC MIND', 'MAVERICK')
    .replaceAll('MAGICMIND', 'MAVERICK');
}

export async function sha256Hash(message: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(message); // Encode the message as UTF-8
  const hashBuffer = await crypto.subtle.digest('SHA-256', data); // Calculate the hash
  const hashArray = Array.from(new Uint8Array(hashBuffer)); // Convert ArrayBuffer to Array of bytes
  const hexHash = hashArray.map((b) => b.toString(16).padStart(2, '0')).join(''); // Convert bytes to hex string
  return hexHash;
}
